import { Splitter, SplitterPanel } from 'primereact/splitter';
import { FC, Suspense, useEffect, useState } from 'react';
import { AssignmentSidebarHeader } from './AssignmentSidebarHeader';
import { useMutation, useQueryLoader } from 'react-relay';
import { createAssignment, getStudentsWithSkillStates } from '../../graphQLOperations/Assignment';
import { AssignmentCreateAssignmentMutation } from '../../graphQLOperations/__generated__/AssignmentCreateAssignmentMutation.graphql';
import { useNavigate } from 'react-router-dom';
import { formatAssignmentDateRange } from '../../businessLogicService/formatAssignmentDateRange';
import { sortClassesList } from '../../utils/sortClassesList';
import { AssignmentStudentsWithSkillStatesQuery } from '../../graphQLOperations/__generated__/AssignmentStudentsWithSkillStatesQuery.graphql';
import { AssignmentSidebarStudentsList } from './AssignmentSidebarStudentsList';
import { AssignmentSidebarDatesForm } from './AssignmentSidebarDatesForm';
import { Button } from 'primereact/button';
import { AssignmentSidebarPreview } from './AssignmentSidebarPreview';
import { Toast } from 'primereact/toast';
import { SelectedSkillForAssignment } from '../../models/Assignment';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedSkillAssignment, selectedAssignmentSkill } from '../../redux/reducers/assignmentSelectedSkillSlice';
import { startOfTomorrow } from 'date-fns';
import { Class } from '../../models/Class';
import { Tooltip } from 'primereact/tooltip';
import { SectionsInfo } from './AssignemntSidebarOpenModal/AssignmentSidebarModalInfo';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { useAssignmentEvent } from '../../custom-hooks/useAssignmentEvent';
import { getAssignmentType } from '../../businessLogicService/getAssignmentType';
import { getSelectedStudentGroups } from '../../businessLogicService/getSelectedStudentGroups';
import { getStudentSelection } from '../../businessLogicService/getStudentSelection';
import { wasSupportSkillWarningShown } from '../../businessLogicService/wasSupportSkillWarningShown';

interface Props {
  selectedSkillForAssignment: SelectedSkillForAssignment;
  toast: Toast | null;
}
export interface Student {
  studentId: string;
  classId: string;
  studentFirstName: string;
  studentLastName: string;
  publicUserId: string;
  selected: boolean;
  selectedClassCount: number;
  noPredPassed?: boolean;
  skills: { id: string; name: string; passed: boolean; readinessScore?: number | null }[];
}

export const studentRowKey = (student: Student) => `${student.studentId}-${student.classId}`;

interface StudentListItem {
  header: string;
  list: Student[];
}

export interface StudentsLists {
  readyToLearn: StudentListItem;
  buildingFoundations: StudentListItem;
  alreadyMastered: StudentListItem;
  [key: string]: StudentListItem;
}

const initialStudentsLists: StudentsLists = {
  readyToLearn: { list: [], header: 'On track' },
  buildingFoundations: { list: [], header: 'Paving the Way' },
  alreadyMastered: { list: [], header: 'Already Passed' },
};

export const AssignmentSidebarContainer: FC<Props> = ({ selectedSkillForAssignment, toast }) => {
  const [assignmentName, setAssignmentName] = useState('');
  const dispatch = useAppDispatch();
  const selectedSkillAssignment = useAppSelector(selectedAssignmentSkill);
  const [invalidAssignmentName, setInvaildAssignmentName] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState<Class[]>([]);
  const [toDate, setToDate] = useState<Date>(startOfTomorrow());
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [notIncludeNoPredPassed, setNotIncludeNoPredPassed] = useState(false);
  /*
   * Reported to analytics, and deliberately separate from notIncludeNoPredPassed: that one tracks
   * whether the exclusion is currently in effect and gets cleared when the teacher re-checks a
   * flagged student, whereas this records that the shortcut was used at all.
   */
  const [doNotIncludeUsed, setDoNotIncludeUsed] = useState(false);
  const [studentsLists, setStudentsLists] = useState<StudentsLists>(initialStudentsLists);
  const [showPreview, setShowPreview] = useState(false);
  const [queryReferenceAssignmentStudents, loadStudentsForAssignment] =
    useQueryLoader<AssignmentStudentsWithSkillStatesQuery>(getStudentsWithSkillStates);

  useEffect(() => {
    if (!selectedClasses.length) {
      setStudentsLists(initialStudentsLists);
    }
  }, [selectedClasses]);

  const goToPreview = () => {
    if (assignmentName.trim() === '') {
      return setInvaildAssignmentName(true);
    }
    setShowPreview(true);
  };

  const hasSelectedStudents = [
    ...studentsLists.alreadyMastered.list,
    ...studentsLists.buildingFoundations.list,
    ...studentsLists.readyToLearn.list,
  ].some(item => item.selected);
  const disablePreviewButton = !hasSelectedStudents || assignmentName.trim() === '' || !selectedClasses.length;
  const navigate = useNavigate();
  const [commitMutation] = useMutation<AssignmentCreateAssignmentMutation>(createAssignment);
  const captureAssignmentEvent = useAssignmentEvent();
  const createAssignmentMutation = () => {
    const itemsByClass = new Map<string, { userId: string; skillIds: string[] }[]>();
    [
      ...studentsLists.alreadyMastered.list,
      ...studentsLists.buildingFoundations.list,
      ...studentsLists.readyToLearn.list,
    ]
      .filter(student => student.selected)
      .forEach(student => {
        const skillIds = selectedSkillForAssignment.onlyTarget
          ? [selectedSkillForAssignment.id]
          : student.skills.filter(sk => !sk.passed || sk.id === selectedSkillForAssignment.id).map(item => item.id);
        if (!skillIds.length) return;
        const classItems = itemsByClass.get(student.classId) ?? [];
        classItems.push({ userId: student.studentId, skillIds });
        itemsByClass.set(student.classId, classItems);
      });

    const classes = sortClassesList(selectedClasses)
      .filter(selectedClass => itemsByClass.has(selectedClass.id))
      .map(selectedClass => ({
        classId: selectedClass.id,
        createAssignmentInputItems: itemsByClass.get(selectedClass.id)!,
      }));
    // Both dates must reach the service as local ISO strings sharing one offset; see
    // formatAssignmentDateRange for why serializing each Date separately breaks across a DST change.
    const { startedOn, dueOn } = formatAssignmentDateRange(fromDate, toDate);

    commitMutation({
      variables: {
        input: {
          classes,
          skillId: selectedSkillForAssignment.id,
          startedOn,
          dueOn,
          name: assignmentName,
        },
      },

      onCompleted: response => {
        if (response.createAssignment.errors?.length) {
          toast?.show({
            severity: 'error',
            closable: true,
            content: (
              <div className="flex align-items-center w-20rem">
                <p className="text-white text-center w-full ">
                  {classes.length > 1
                    ? ' Assignments were not created successfully'
                    : ' Assignment was not created successfully'}
                </p>
              </div>
            ),
          });
        } else {
          /*
           * One event per assignment: the mutation creates one per class, so the per-class classId
           * is what each event reports. Every other property describes the sidebar session as a
           * whole — the support-skill warning and the "Do Not Include" shortcut span all selected
           * classes — so they are computed once and repeated.
           */
          classes.forEach(({ classId }) => {
            captureAssignmentEvent('assignment_created', {
              entry_point: selectedSkillForAssignment.entryPoint,
              assignment_type: getAssignmentType(selectedSkillForAssignment.onlyTarget),
              student_groups_selected: getSelectedStudentGroups(studentsLists),
              student_selection: getStudentSelection(studentsLists),
              support_skill_warning_shown: wasSupportSkillWarningShown(studentsLists),
              do_not_include_used: doNotIncludeUsed,
              class_id: classId,
              skill_id: selectedSkillForAssignment.code,
            });
          });
          toast?.show({
            severity: 'success',
            closable: true,
            content: (
              <div className="w-full flex align-items-center w-20rem">
                <i className="nc_icon-nc_confirm icon-md" />
                <p className="text-white">
                  {classes.length > 1
                    ? `${classes.length} assignments were created successfully, one per class.`
                    : 'The assignment was created successfully.'}{' '}
                  <span className="underline cursor-pointer" onClick={() => navigate('/assignments')}>
                    Go to Monitor{' '}
                  </span>
                  {classes.length > 1 ? 'to view them.' : 'to view it.'}
                </p>
              </div>
            ),
          });
        }
        dispatch(setSelectedSkillAssignment(null));
      },
    });
  };

  return (
    <Splitter
      style={{
        top: 96,
        bottom: 75,
        height: 'auto',
        position: 'fixed',
        width: '100vw',
        zIndex: 3,
        backgroundColor: 'transparent',
      }}
    >
      <SplitterPanel style={{ backgroundColor: 'black', opacity: 0.3 }}></SplitterPanel>
      <SplitterPanel style={{ backgroundColor: 'white', opacity: 1, maxWidth: '75vw', padding: 0 }}>
        <div className="p-0 m-0 w-full h-full flex flex-column">
          <div className="flex justify-content-between align-items-center px-4 py-3 flex-shrink-0 surface-0 border-bottom-1 surface-border">
            <div className="flex align-items-center">
              <span className="nc_assignment-header-icon mr-3">
                <i className="nc_icon-nc_target icon-sm" />
              </span>
              <div>
                <h3 className="m-0 font-semibold">
                  {selectedSkillAssignment.skill?.onlyTarget ? 'Assign a skill' : 'Assign a Skill Pathway'}
                </h3>
                <p className="m-0 text-color-secondary">
                  {selectedSkillForAssignment.name} ({selectedSkillForAssignment.code})
                </p>
              </div>
            </div>
            <div className="flex align-items-center">
              <Tooltip className="w-4 h-20rem" position="bottom" target="#info-assignment">
                <SectionsInfo onlyTarget={selectedSkillForAssignment.onlyTarget || false} />
              </Tooltip>
              <i id="info-assignment" className="custom-target-icon nc_icon-nc_info icon-sm mr-3" />
              <i
                className="nc_icon-nc_close icon-sm cursor-pointer"
                onClick={() => dispatch(setSelectedSkillAssignment(null))}
              />
            </div>
          </div>
          <div className="flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
            {showPreview && <h3 className="font-semibold px-4 mt-4 mb-0">Assignment preview:</h3>}
            <div className="px-4">
              <AssignmentSidebarHeader
                invalidName={invalidAssignmentName}
                setInvalidName={setInvaildAssignmentName}
                setAssignmentName={setAssignmentName}
                assignmentName={assignmentName}
                previewEnabled={showPreview}
                setSelectedClasses={setSelectedClasses}
                selectedClasses={selectedClasses}
                loadQuery={loadStudentsForAssignment}
                selectedSkillForAssignment={selectedSkillForAssignment}
              />
            </div>

            {showPreview && (
              <AssignmentSidebarPreview
                selectedClasses={selectedClasses}
                fromDate={fromDate || new Date()}
                toDate={toDate}
                selectedSkillForAssignment={selectedSkillForAssignment}
                studentsLists={studentsLists}
              />
            )}
            {
              <div className="px-4" style={{ display: `${showPreview ? 'none' : ''}` }}>
                <div className="w-full">
                  {queryReferenceAssignmentStudents && selectedClasses.length ? (
                    <Suspense
                      fallback={
                        <div style={{ height: '30vh' }} className="flex justify-content-center align-items-center">
                          <ThreeDotLoader />
                        </div>
                      }
                    >
                      <AssignmentSidebarStudentsList
                        selectedClasses={selectedClasses}
                        studentsLists={studentsLists}
                        notIncludeNoPredPassed={notIncludeNoPredPassed}
                        setNotIncludeNoPredPassed={setNotIncludeNoPredPassed}
                        markDoNotIncludeUsed={() => setDoNotIncludeUsed(true)}
                        setStudentsLists={setStudentsLists}
                        queryReference={queryReferenceAssignmentStudents}
                      />
                    </Suspense>
                  ) : (
                    <div style={{ height: '30vh' }} className="flex justify-content-center align-items-center">
                      <span className="text-color-secondary">Select a class</span>
                    </div>
                  )}
                </div>
                <hr />
                <div className="mb-4">
                  <AssignmentSidebarDatesForm
                    toDate={toDate}
                    setToDate={setToDate}
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                  />
                </div>
              </div>
            }
          </div>

          <div className="flex justify-content-between align-items-center p-3 flex-shrink-0 surface-0 border-top-1 surface-border">
            <Button link label="Cancel" onClick={() => dispatch(setSelectedSkillAssignment(null))} />
            {showPreview ? (
              <div className="flex gap-2">
                <Button icon="nc_icon-nc_back icon-sm" label="Back" outlined onClick={() => setShowPreview(false)} />
                <Button
                  icon="nc_icon-nc_teacher-assigned icon-sm"
                  label="Assign"
                  onClick={() => createAssignmentMutation()}
                />
              </div>
            ) : (
              <Button
                disabled={disablePreviewButton}
                onClick={() => goToPreview()}
                label="Generate preview"
                icon="nc_icon-nc_next icon-sm"
                iconPos="right"
              />
            )}
          </div>
        </div>
      </SplitterPanel>
    </Splitter>
  );
};
